import { Injectable } from '@angular/core';
import { OAuthCompleteMessage } from '../../interfaces';

/**
 * Error thrown when the OAuth popup is closed (by the user or the browser)
 * before a completion message is received. Consumers typically treat this as a
 * silent cancellation.
 */
export class OAuthPopupClosedError extends Error {
  constructor() {
    super('OAuth popup was closed before completing.');
    this.name = 'OAuthPopupClosedError';
  }
}

/** Error thrown when the browser refuses to open the popup (e.g. blocked). */
export class OAuthPopupBlockedError extends Error {
  constructor() {
    super('Unable to open the authorization popup. It may have been blocked.');
    this.name = 'OAuthPopupBlockedError';
  }
}

const POPUP_WIDTH = 600;
const POPUP_HEIGHT = 700;
const POPUP_CLOSED_POLL_MS = 500;

/**
 * Drives the OAuth authorization popup handshake used by object-storage OAuth
 * connections.
 *
 * The flow: open a centered popup at the provider's authorization URL, then
 * listen for the `postMessage` that {@link OauthCompletePageComponent} sends
 * back from `/oauth/complete`. Only messages from the same origin and of type
 * `OAUTH_COMPLETE` are accepted. The message listener, the closed-popup poll,
 * and the popup window itself are always cleaned up before the returned promise
 * settles.
 *
 * Extracted from `add-connection-dialog.component.ts` so the popup/message
 * plumbing lives in one tested, reusable place.
 */
@Injectable({ providedIn: 'root' })
export class OAuthFlowService {
  /**
   * Opens the authorization popup and resolves with the completion message.
   *
   * Resolves with the {@link OAuthCompleteMessage} once received — note a
   * resolved message may still carry `success: false` (the provider denied
   * authorization); that is not an error. Rejects with
   * {@link OAuthPopupBlockedError} if the popup cannot be opened, or
   * {@link OAuthPopupClosedError} if the user closes it first.
   */
  open(authorizationUrl: string): Promise<OAuthCompleteMessage> {
    return new Promise<OAuthCompleteMessage>((resolve, reject) => {
      const popup = this.#openCenteredPopup(authorizationUrl);
      if (!popup) {
        reject(new OAuthPopupBlockedError());
        return;
      }

      let closedPoll: ReturnType<typeof setInterval> | null = null;

      const cleanup = (): void => {
        window.removeEventListener('message', handler);
        if (closedPoll !== null) {
          clearInterval(closedPoll);
          closedPoll = null;
        }
        if (popup && !popup.closed) {
          popup.close();
        }
      };

      const handler = (event: MessageEvent): void => {
        // Only accept the message from our own /oauth/complete popup: the origin
        // must match exactly AND the sender must be the window we opened, so a
        // different same-origin tab/frame can't complete the flow.
        if (event.origin !== window.location.origin || event.source !== popup) {
          return;
        }
        const message = event.data as OAuthCompleteMessage;
        if (message?.type !== 'OAUTH_COMPLETE') {
          return;
        }
        cleanup();
        resolve(message);
      };

      window.addEventListener('message', handler);

      // The popup may be dismissed without ever posting a message; detect that
      // so the promise settles and the listener is removed instead of leaking.
      closedPoll = setInterval(() => {
        if (popup.closed) {
          cleanup();
          reject(new OAuthPopupClosedError());
        }
      }, POPUP_CLOSED_POLL_MS);
    });
  }

  #openCenteredPopup(url: string): Window | null {
    const left = window.screenX + (window.outerWidth - POPUP_WIDTH) / 2;
    const top = window.screenY + (window.outerHeight - POPUP_HEIGHT) / 2;
    return window.open(
      url,
      'oauth_popup',
      `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},resizable,scrollbars`,
    );
  }
}
